/**
 * Downloads the files for a paid model report: lists the AVV and RAW documents held in storage
 * and serves them individually or as a watermarked zip.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiFolder } from 'react-icons/fi';
import { useAuth, supabase } from '../context/AuthContext';
import JSZip from 'jszip';
import { PDFDocument, rgb, degrees } from "pdf-lib";
import CustomerLayout from './CustomerLayout';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

const DownloadReportFiles = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [files, setFiles] = useState({ avv: [], raw: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [storagePath, setStoragePath] = useState('');
  const [reportDetails, setReportDetails] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingAvv, setDownloadingAvv] = useState(false);
  const [downloadingRaw, setDownloadingRaw] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (user && id) {
      fetchReportDetails();
    }
  }, [user, id]);

  useEffect(() => {
    if (storagePath) {
      fetchFiles();
    }
  }, [storagePath]);

  // Loads the report, refuses access unless it is paid for, and captures its storage path
  const fetchReportDetails = async () => {
    try {
      // First, check if the user is authenticated
      if (!user || !user.id) {
        setError('You must be logged in to access this page.');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('modelreports')
        .select(`
          *,
          customer_model_reports!modelreports_mr_id_fkey (
            storage_path,
            mr_approval
          )
        `)
        .eq('id', id)
        .eq('user_id', user.id) // CRITICAL: Only fetch reports belonging to the current user
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned - either report doesn't exist or user doesn't have access
          setError('Report not found or you do not have permission to access this report.');
        } else {
          throw error;
        }
        setLoading(false);
        return;
      }

      if (data) {
        // Fetch transaction status separately
        const { data: transactionData, error: transactionError } = await supabase
          .from('transactions')
          .select('payment_status')
          .eq('submission_id', id)
          .single();

        if (transactionError) {
          console.error('Error fetching transaction:', transactionError);
          setError('Error checking payment status. Please try again later.');
          setLoading(false);
          return;
        }

        // Check if payment status is not 'Paid'
        if (transactionData?.payment_status !== 'Paid') {
          setError('You do not have enough permission to access this page. Please complete the payment first.');
          setLoading(false);
          return;
        }

        setReportDetails(data);
        if (data.customer_model_reports?.storage_path) {
          setStoragePath(data.customer_model_reports.storage_path);
        } else {
          setError('No storage path found for this report.');
        }
      } else {
        setError('Report not found or you do not have permission to access this report.');
      }
    } catch (err) {
      console.error('Error fetching report details:', err);
      setError('Error fetching report details. Please try again later.');
    }
  };

  // Fetches the list of files in the AVV and RAW folders for the report, if they exist
  const fetchFiles = async () => {
    try {
      // Additional security check - ensure user is authenticated and has access to this report
      if (!user || !user.id || !reportDetails) {
        setError('You do not have permission to access these files.');
        setLoading(false);
        return;
      }

      // Verify that the report belongs to the current user (double-check)
      if (reportDetails.user_id !== user.id) {
        setError('You do not have permission to access these files.');
        setLoading(false);
        return;
      }

      if (!storagePath) {
        setError('No storage path available.');
        setLoading(false);
        return;
      }

      const [{ data: avvData, error: avvError }, { data: rawData, error: rawError }] = await Promise.all([
        supabase.storage.from('reports').list(`${storagePath}/AVV DOCUMENTS`),
        supabase.storage.from('reports').list(`${storagePath}/RAW DOCUMENTS`),
      ]);

      if (avvError) {
        console.error('Error fetching AVV documents:', avvError);
      }

      if (rawError) {
        console.error('Error fetching RAW documents:', rawError);
      }

      // A report legitimately may have only one of the two folders, so fail only if both are unreadable
      if (avvError && rawError) {
        throw new Error('Failed to fetch both AVV and RAW documents');
      }

      setFiles({
        avv: avvData || [],
        raw: rawData || []
      });
    } catch (err) {
      setError('Error fetching files. Please try again later.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Adds a watermark to each page of a PDF file. 
   * The watermark text includes the unique ID of the report.
   */
  async function addWatermarkToPDF(pdfBuffer, uniqueId) {
    const pdfDoc = await PDFDocument.load(pdfBuffer);
    const pages = pdfDoc.getPages();
    const { width, height } = pages[0].getSize();
    for (const page of pages) {
      page.drawText(`UNIQUE - ID: ${uniqueId}`, {
        x: 90,
        y: 90,
        size: 55,
        color: rgb(0.6, 0.6, 0.6),
        opacity: 0.4,
        rotate: degrees(50)
      });
    }

    return await pdfDoc.save();
  }


  // Single-file download; PDFs are watermarked in the browser before being handed to the user
  const downloadFile = async (fileName, type) => {
    try {
      // Security check - ensure user has permission to download files
      if (!user || !user.id || !reportDetails || reportDetails.user_id !== user.id) {
        setError("You do not have permission to download these files.");
        return;
      }

      if (!storagePath || !reportDetails?.customer_unique_id) {
        throw new Error("No storage path or unique ID available");
      }

      const folder = type === "avv" ? "AVV DOCUMENTS" : "RAW DOCUMENTS";
      const filePath = `${storagePath}/${folder}/${fileName}`;

      // Fetch the file from Supabase Storage
      const { data, error } = await supabase.storage.from("reports").download(filePath);
      if (error) throw error;

      const fileType = fileName.split('.').pop().toLowerCase();
      let modifiedFile;

      if (fileType === "pdf") {
        modifiedFile = await addWatermarkToPDF(await data.arrayBuffer(), reportDetails.customer_unique_id);
      } else {
        modifiedFile = data; // Other files remain unchanged
      }
      const url = URL.createObjectURL(new Blob([modifiedFile]));
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Error downloading file:", err);
      setError("Error downloading file. Please try again.");
    }
  };


  const setDownloadingState = (scope, value) => {
    if (scope === 'avv') setDownloadingAvv(value);
    else if (scope === 'raw') setDownloadingRaw(value);
    else setDownloadingAll(value);
  };

  const downloadFilesByScope = async (scope = 'all') => {
    let downloadingDiv = null;

    try {
      // Security check - ensure user has permission to download files
      if (!user || !user.id || !reportDetails || reportDetails.user_id !== user.id) {
        setError("You do not have permission to download these files.");
        return;
      }

      if (!storagePath || !reportDetails?.customer_unique_id) {
        throw new Error("No storage path or unique ID available");
      }

      const includeAvv = scope === 'all' || scope === 'avv';
      const includeRaw = scope === 'all' || scope === 'raw';
      const filesToDownload = [
        ...(includeAvv ? files.avv.map((file) => ({ file, type: 'avv' })) : []),
        ...(includeRaw ? files.raw.map((file) => ({ file, type: 'raw' })) : []),
      ];

      if (filesToDownload.length === 0) {
        setError(
          scope === 'avv'
            ? 'No AVV documents available to download.'
            : scope === 'raw'
              ? 'No RAW documents available to download.'
              : 'No files available to download.'
        );
        return;
      }

      setDownloadingState(scope, true);

      downloadingDiv = document.createElement("div");
      downloadingDiv.className = "fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded shadow-lg z-50";
      downloadingDiv.textContent = "Preparing files for download...";
      document.body.appendChild(downloadingDiv);

      const zip = new JSZip();
      const avvFolder = includeAvv ? zip.folder("AVV DOCUMENTS") : null;
      const rawFolder = includeRaw ? zip.folder("RAW DOCUMENTS") : null;

      for (const { file, type } of filesToDownload) {
        const filePath = `${storagePath}/${type === "avv" ? "AVV DOCUMENTS" : "RAW DOCUMENTS"}/${file.name}`;

        // Fetch file from Supabase
        const { data, error } = await supabase.storage.from("reports").download(filePath);
        if (error) throw error;

        const fileType = file.name.split('.').pop().toLowerCase();
        let modifiedFile;

        if (fileType === "pdf") {
          modifiedFile = await addWatermarkToPDF(await data.arrayBuffer(), reportDetails.customer_unique_id);
        } else {
          modifiedFile = data;
        }

        const folder = type === "avv" ? avvFolder : rawFolder;
        folder.file(file.name, modifiedFile);
      }

      downloadingDiv.textContent = "Creating zip file...";
      const content = await zip.generateAsync({ type: "blob" });

      const uniqueId = reportDetails?.customer_unique_id || "report";
      const zipName =
        scope === 'avv'
          ? `${uniqueId}_AVV_documents.zip`
          : scope === 'raw'
            ? `${uniqueId}_RAW_documents.zip`
            : `${uniqueId}_files.zip`;

      const url = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = zipName;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Error downloading files:", err);
      setError("Error downloading files. Please try again.");
    } finally {
      if (downloadingDiv?.parentNode) {
        document.body.removeChild(downloadingDiv);
      }
      setDownloadingState(scope, false);
    }
  };

  const downloadAllFiles = () => downloadFilesByScope('all');
  const downloadAvvFiles = () => downloadFilesByScope('avv');
  const downloadRawFiles = () => downloadFilesByScope('raw');


  const handleBackToDashboard = () => {
    navigate(`/dashboard?highlightOrderId=${encodeURIComponent(id)}`, {
      state: { highlightFromDownload: true },
    });
  };

  const pageSubtitle = 'Download AVV and RAW documents for your approved report.';
  const hasFiles = files.avv.length > 0 || files.raw.length > 0;
  const totalFileCount = files.avv.length + files.raw.length;
  const isAnyZipDownloading = downloadingAll || downloadingAvv || downloadingRaw;

  const sectionDownloadButtonClass =
    'inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70 sm:text-sm';

  const renderSectionDownloadButton = (scope, label, shortLabel, isDownloading, fileCount) => (
    <button
      type="button"
      onClick={() => (scope === 'avv' ? downloadAvvFiles() : downloadRawFiles())}
      disabled={isAnyZipDownloading || fileCount === 0}
      className={sectionDownloadButtonClass}
      title={label}
      aria-label={label}
    >
      {isDownloading ? (
        <>
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
          Preparing...
        </>
      ) : (
        <>
          <ArrowDownTrayIcon className="h-4 w-4" />
          <span className="md:hidden">{shortLabel}</span>
          <span className="hidden md:inline">{label}</span>
        </>
      )}
    </button>
  );

  const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    return `${(bytes / 1024).toFixed(2)} KB`;
  };

  const formatFileDate = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleDateString();
  };

  if (loading) {
    return (
      <CustomerLayout
        activeItemId="dashboard"
        title="Documents"
        subtitle="Loading documents..."
        contentClassName="mx-auto w-full max-w-none"
      >
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </CustomerLayout>
    );
  }

  if (error && !reportDetails) {
    return (
      <CustomerLayout
        activeItemId="dashboard"
        title="Documents"
        subtitle="Unable to access documents."
        contentClassName="mx-auto w-full max-w-2xl"
      >
        <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="rounded-xl border-l-4 border-red-400 bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-red-800">Access Denied</h3>
                <p className="mt-2 text-red-700">{error}</p>
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-center">
            <button
              onClick={handleBackToDashboard}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout
      activeItemId="dashboard"
      title="Documents"
      subtitle={pageSubtitle}
      contentClassName="mx-auto w-full max-w-none"
    >
      <section className="mb-4 overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-sm sm:mb-6 lg:rounded-[2rem]">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 sm:px-6">
          <h2 className="min-w-0 text-md font-medium uppercase tracking-[0.04em] text-slate-900">
            Report Details
          </h2>

          <button
            type="button"
            onClick={handleBackToDashboard}
            className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:gap-2 sm:px-4"
          >
            <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="sm:hidden">Back</span>
            <span className="hidden sm:inline">Back to Dashboard</span>
          </button>
        </div>

        <div className="px-4 py-5 sm:px-6">
          <dl className="grid gap-4 sm:grid-cols-3">
            <div className="min-w-0 rounded-[1rem] border border-slate-200 bg-slate-50/80 px-4 py-3">
              <dt className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-500">
                Unique ID
              </dt>
              <dd className="mt-1 truncate text-sm font-semibold text-slate-900 sm:text-base">
                {reportDetails?.customer_unique_id || 'N/A'}
              </dd>
            </div>
            <div className="min-w-0 rounded-[1rem] border border-slate-200 bg-slate-50/80 px-4 py-3">
              <dt className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-500">
                MRE Approval
              </dt>
              <dd className="mt-1 truncate text-sm font-semibold text-slate-900 sm:text-base">
                {reportDetails?.customer_model_reports?.mr_approval || 'N/A'}
              </dd>
            </div>
            <div className="min-w-0 rounded-[1rem] border border-slate-200 bg-slate-50/80 px-4 py-3">
              <dt className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-500">
                VIN
              </dt>
              <dd className="mt-1 truncate text-sm font-semibold text-slate-900 sm:text-base">
                {reportDetails?.vin_number || 'N/A'}
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-sm lg:rounded-[2rem]">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <h2 className="text-md font-medium uppercase tracking-[0.06em] text-slate-900">
              Available Files
            </h2>
            <p className="mt-1 hidden text-sm text-slate-500 md:block">
              Download individual files or download everything at once.
            </p>
          </div>
          {hasFiles && (
            <div className="flex shrink-0 items-center justify-end gap-3">
              <span className="hidden text-sm text-slate-500 sm:mr-1 sm:inline">
                {totalFileCount} file{totalFileCount === 1 ? '' : 's'}
              </span>
              <button
                type="button"
                onClick={downloadAllFiles}
                disabled={isAnyZipDownloading}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70 sm:px-4"
              >
                {downloadingAll ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Preparing...
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon className="h-5 w-5" />
                    <span className="md:hidden">All Files</span>
                    <span className="hidden md:inline">Download All Files</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        <div className="p-4 sm:p-6">
          {error && (
            <div className="mb-4 rounded-xl border-l-4 border-red-400 bg-red-50 p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {!hasFiles ? (
            <div className="py-12 text-center">
              <FiFolder className="mx-auto h-10 w-10 text-slate-400" />
              <h3 className="mt-3 text-sm font-medium text-slate-900">No files found</h3>
              <p className="mt-1 text-sm text-slate-500">
                No files are available in any folder.
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              <div>
                <div className="mb-4 flex items-center justify-between gap-3">
                  <h3 className="flex min-w-0 items-center gap-2 text-sm font-medium uppercase tracking-[0.06em] text-slate-700">
                    <DocumentTextIcon className="h-5 w-5 shrink-0 text-blue-600" />
                    <span className="truncate">AVV Documents</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                      {files.avv.length}
                    </span>
                  </h3>
                  {renderSectionDownloadButton('avv', 'Download AVV Docs', 'AVV Docs', downloadingAvv, files.avv.length)}
                </div>
                {files.avv.length === 0 ? (
                  <p className="text-sm text-slate-500">No AVV documents available.</p>
                ) : (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {files.avv.map((file) => (
                      <div
                        key={file.name}
                        className="group rounded-[1rem] border border-slate-200 bg-slate-50/80 p-4 shadow-sm transition hover:border-blue-300 hover:shadow-md"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 items-start gap-3">
                            <div className="rounded-lg bg-blue-50 p-2 transition-colors group-hover:bg-blue-100">
                              <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-slate-900" title={file.name}>
                                {file.name}
                              </p>
                              <p className="mt-1 text-xs text-slate-500">
                                {formatFileSize(file.metadata?.size)} · {formatFileDate(file.metadata?.lastModified)}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => downloadFile(file.name, 'avv')}
                            className="shrink-0 rounded-full p-2 text-blue-600 transition hover:bg-blue-50 hover:text-blue-800"
                            title="Download file"
                          >
                            <ArrowDownTrayIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="mb-4 flex items-center justify-between gap-3">
                  <h3 className="flex min-w-0 items-center gap-2 text-sm font-medium uppercase tracking-[0.06em] text-slate-700">
                    <DocumentTextIcon className="h-5 w-5 shrink-0 text-blue-600" />
                    <span className="truncate">RAW Documents</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                      {files.raw.length}
                    </span>
                  </h3>
                  {renderSectionDownloadButton('raw', 'Download RAW Docs', 'RAW Docs', downloadingRaw, files.raw.length)}
                </div>
                {files.raw.length === 0 ? (
                  <p className="text-sm text-slate-500">No RAW documents available.</p>
                ) : (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {files.raw.map((file) => (
                      <div
                        key={file.name}
                        className="group rounded-[1rem] border border-slate-200 bg-slate-50/80 p-4 shadow-sm transition hover:border-blue-300 hover:shadow-md"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 items-start gap-3">
                            <div className="rounded-lg bg-blue-50 p-2 transition-colors group-hover:bg-blue-100">
                              <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-slate-900" title={file.name}>
                                {file.name}
                              </p>
                              <p className="mt-1 text-xs text-slate-500">
                                {formatFileSize(file.metadata?.size)} · {formatFileDate(file.metadata?.lastModified)}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => downloadFile(file.name, 'raw')}
                            className="shrink-0 rounded-full p-2 text-blue-600 transition hover:bg-blue-50 hover:text-blue-800"
                            title="Download file"
                          >
                            <ArrowDownTrayIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </CustomerLayout>
  );
};

export default DownloadReportFiles; 
